import { useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Check, ChevronsUpDown, CircleSmall, Plus, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CreateOrganizationDialog } from "@/components/CreateOrganizationDialog";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useTranslation } from "@/hooks/useTranslation";
import { toast } from "@/hooks/useToast";
import { EntityImage } from "@/components/ui/entity-image";

interface OrgLogoProps {
  logoPath: string | null | undefined;
  name: string;
  className?: string;
}

function OrgLogo({ logoPath, name, className = "h-4 w-4" }: OrgLogoProps) {
  if (logoPath) {
    return (
      <EntityImage
        imagePath={logoPath}
        alt={name}
        className={cn("aspect-square shrink-0 !rounded-sm", className)}
        variant="square"
      />
    );
  }
  return <CircleSmall className={cn("text-muted-foreground shrink-0", className)} />;
}

interface OrganizationSwitcherProps {
  trigger?: ReactNode | ((props: { open: boolean }) => ReactNode);
}

export function OrganizationSwitcher({ trigger }: OrganizationSwitcherProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const {
    selectedOrganization,
    organizations,
    setSelectedOrganization,
    toggleStarred,
    loading: isLoading,
    refreshOrganizations,
    recentOrgIds,
    addToRecent
  } = useOrganization();
  const { t } = useTranslation();

  const handleOrganizationCreated = async (orgId: string) => {
    // Refresh org list first so OrgLayout can find the new org and won't redirect to 404
    await refreshOrganizations();
    setOpen(false);
    setCreateDialogOpen(false);
    navigate(`/org/${orgId}/dashboard`);
  };

  if (isLoading) {
    return (
      <Button
        variant="outline"
        className="w-full max-w-[280px] justify-between bg-background border-border"
        disabled
      >
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{t('organizations.loading')}</span>
        </div>
      </Button>
    );
  }

  // If no organizations exist, show create organization button
  if (organizations.length === 0) {
    return (
      <>
        <Button
          variant="outline"
          className="w-full max-w-[280px] justify-start gap-2 bg-background border-border"
          onClick={() => setCreateDialogOpen(true)}
        >
          <Plus className="h-4 w-4" />
          <span className="text-sm">{t('organizations.createNew')}</span>
        </Button>
        
        {/* Conditionally mounted: the dialog runs data hooks (templates query),
            which must not fire on every page load (CLAUDE.md). */}
        {createDialogOpen && (
          <CreateOrganizationDialog
            open={createDialogOpen}
            onOpenChange={setCreateDialogOpen}
            onOrganizationCreated={handleOrganizationCreated}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {typeof trigger === 'function' ? trigger({ open }) : trigger || (
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full sm:w-auto sm:min-w-[280px] justify-between bg-background border-border"
            >
              <div className="flex items-center gap-2">
                <OrgLogo
                  logoPath={selectedOrganization?.logo_small}
                  name={selectedOrganization?.name || ''}
                />
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium whitespace-nowrap">{selectedOrganization?.name}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {selectedOrganization?.description || t('organizations.organization')}
                  </span>
                </div>
              </div>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[280px] max-w-[320px] p-0 border-success" align="start" alignOffset={1}>
          <Command>
            <CommandInput placeholder={t('organizations.searchPlaceholder')} />
            <CommandList>
              <CommandEmpty>{t('organizations.noOrganizationsFound')}</CommandEmpty>
              {/* Recent Organizations */}
              {recentOrgIds.length > 0 && (
                <CommandGroup heading="Recent">
                  {recentOrgIds
                    .map(id => organizations.find(org => org.id === id))
                    .filter(Boolean)
                    .map((org) => (
                      <CommandItem
                        key={`recent-${org!.id}`}
                        value={`recent-${org!.name}`}
                        className={cn(
                          selectedOrganization?.id === org!.id && "bg-success/10"
                        )}
                        onSelect={() => {
                          addToRecent(org!.id);
                          setSelectedOrganization(org!);
                          setOpen(false);
                          toast.success(t('organizations.switched', { name: org!.name }));
                        }}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <OrgLogo logoPath={org!.logo_small} name={org!.name} />
                          <span className="text-sm font-medium whitespace-nowrap">{org!.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStarred(org!);
                          }}
                        >
                          <Star className={cn(
                            "h-4 w-4",
                            org!.is_starred ? "fill-amber-500 text-amber-500" : "text-muted-foreground hover:text-amber-500"
                          )} />
                        </Button>
                        <Check
                          className={cn(
                            "h-4 w-4",
                            selectedOrganization?.id === org!.id ? "opacity-100 text-success" : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}
              {/* Starred Organizations */}
              {organizations.some(org => org.is_starred) && (
                <CommandGroup heading="Starred">
                  {organizations.filter(org => org.is_starred).map((org) => (
                    <CommandItem
                      key={org.id}
                      value={org.name}
                      className={cn(
                        selectedOrganization?.id === org.id && "bg-success/10"
                      )}
                      onSelect={() => {
                        addToRecent(org.id);
                        setSelectedOrganization(org);
                        setOpen(false);
                        toast.success(t('organizations.switched', { name: org.name }));
                      }}
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <OrgLogo logoPath={org.logo_small} name={org.name} />
                        <span className="text-sm font-medium whitespace-nowrap">{org.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStarred(org);
                        }}
                      >
                        <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                      </Button>
                      <Check
                        className={cn(
                          "h-4 w-4",
                          selectedOrganization?.id === org.id ? "opacity-100 text-success" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {/* Other Organizations */}
              <CommandGroup heading={organizations.some(org => org.is_starred) || recentOrgIds.length > 0 ? "All Organizations" : undefined}>
                {organizations.filter(org => !org.is_starred).map((org) => (
                  <CommandItem
                    key={org.id}
                    value={org.name}
                    className={cn(
                      selectedOrganization?.id === org.id && "bg-success/10"
                    )}
                    onSelect={() => {
                      addToRecent(org.id);
                      setSelectedOrganization(org);
                      setOpen(false);
                      toast.success(t('organizations.switched', { name: org.name }));
                    }}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <OrgLogo logoPath={org.logo_small} name={org.name} />
                      <span className="text-sm font-medium">{org.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStarred(org);
                      }}
                    >
                      <Star className="h-4 w-4 text-muted-foreground hover:text-amber-500" />
                    </Button>
                    <Check
                      className={cn(
                        "h-4 w-4",
                        selectedOrganization?.id === org.id ? "opacity-100 text-success" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
              <div className="border-t p-2">
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-2 text-sm"
                  onClick={() => {
                    setOpen(false);
                    setCreateDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4" />
                  {t('organizations.createNew')}
                </Button>
              </div>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      {/* Conditionally mounted: the dialog runs data hooks (templates query),
          which must not fire on every page load (CLAUDE.md). */}
      {createDialogOpen && (
        <CreateOrganizationDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onOrganizationCreated={handleOrganizationCreated}
        />
      )}
    </>
  );
}